/**
 * 物件ストア (Pinia) — 避難マップ (v1) 用。
 *
 * 永続化方式は Firebase 設定 (§4.3) の有無で分岐する (propertyStoreV2 と同型):
 * - 未設定 (isFirebaseConfigured()===false): localStorage (persisted-crud)。
 * - 設定済み: Firestore (firestore-crud)。auth (§7) の status が 'ready' になるまで
 *   crud を生成しない (email 未確定のまま Firestore へアクセスしないため)。
 *
 * v1 の編集は全て updateProperty 経由で property を直接 mutation しないため、
 * updatedAt は crud.update が更新する — v2 のような touch() は不要。
 */
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import type {
  DraftProperty,
  EvacuationLink,
  Property,
  LngLat,
  PropertyKind,
} from '@/types/property';
// 同梱の既定物件 (東京/横浜/名古屋/大阪/福岡)。避難先ポリゴンと実歩行経路まで
// 焼き込み済みなので、実行時に OSM/Directions を引く必要はない。
// 生成: scripts/bake-seeds.mjs
import seedProperties from '@/data/evacuation-seeds.json';
import { migrateLegacyPoiCategories } from '@/lib/map/poi-reference';
import { cleanPlaceLabel } from '@/lib/evacuation/place-label';
import { sanitizeWaypoints } from '@/lib/evacuation/waypoints';
import { snapWaypointsForRoute } from '@/lib/geo/route-waypoints';
import { areaAsRings } from '@/lib/evacuation/place-polygon';
import { currentEditor, withEditStamp } from './edit-stamp';
import { loadEvacuationPlaces } from '@/services/evacuationData';
import { generateId, nowIso } from '@/lib/id';
import { polygonCentroid } from '@/lib/geo/polygon-utils';
// 経路候補の生成 (直進 alternatives + 迂回 + 橋認識 + 境界切詰 + 重複排除) は
// v2 (災害時避難先) と同一ロジックを共有する (map 非依存の純ロジック)。
import {
  fetchAccurateRouteCandidates,
  type AccurateRouteResult,
} from '@/lib/evacuation-sites/autodraft';
import { createPersistedCrud, type PersistedCrud } from '@/lib/stores/persisted-crud';
import { createFirestoreCrud, type FirestoreCrud } from '@/lib/stores/firestore-crud';
import { isFirebaseConfigured } from '@/lib/firebase/init';
import { ensureSeedsV1 } from '@/lib/firebase/bootstrap';
import { useAuthStore } from '@/stores/authStore';
import type { SaveState } from '@/lib/firebase/save-pipeline';

const STORAGE_KEY = 'evacuation-map.properties.v1';

/**
 * 同梱の既定物件 (東京/横浜/名古屋/大阪/福岡)。
 *
 * **初回のみ投入し、以後は上書きしない。** 保存された時点でユーザーのデータになる。
 * 以前は SAMPLE_VERSION という定数を持ち、それを bump すると保存済みデータを同梱
 * シードで上書きして「シードの修正を全クライアントへ配る」仕組みだったが、
 * ユーザーの編集内容を開発側の都合で書き換える経路でもあった (実際に手動ラベル位置を
 * 潰しかけた)。シードの誤りは焼き込み前に検証する運用に切り替え、この経路は廃止した。
 */

/**
 * UI から除外した旧オプションを持つ既存 localStorage データを吸収する。
 *   - buildingIcon 'tower' は BuildingMarker 上 'default' と同一形状のため 'default' へ
 *   - routeMode 'manual' は未実装 (auto と同挙動) のため 'auto' へ
 * 型 union からは消していない (破壊的でない) が、 UI 選択肢との齟齬を防ぐため読み込み時に正規化。
 */
function normalizeLegacyProperty(p: Property): Property {
  let next = p;
  if ((p.buildingIcon as string) === 'tower') {
    next = { ...next, buildingIcon: 'default' };
  }
  if ((p.evacuation?.routeMode as string) === 'manual') {
    next = {
      ...next,
      evacuation: { ...next.evacuation, routeMode: 'auto' },
    };
  }
  // 参考ラベルの後方互換移行 (旧 'medical'→hospital+pharmacy、
  // 'supermarket'→supermarket+building)。移行不要なら同一参照が返る。
  if (next.referenceLabels?.categories) {
    const migrated = migrateLegacyPoiCategories(next.referenceLabels.categories);
    if (migrated !== next.referenceLabels.categories) {
      next = {
        ...next,
        referenceLabels: { ...next.referenceLabels, categories: migrated },
      };
    }
  }
  // 避難先ラベルを表示用に整形 (公式名の行政接頭辞・棟・階/ハザード注記を落とす)。
  // placeName は選択専用の表示スナップショット (手動編集不可) なので無条件・冪等に適用してよい。
  if (next.evacuation?.placeName) {
    const cleaned = cleanPlaceLabel(next.evacuation.placeName);
    if (cleaned !== next.evacuation.placeName) {
      next = {
        ...next,
        evacuation: { ...next.evacuation, placeName: cleaned },
      };
    }
  }
  // 経由点 (waypoints) を安全化する。型は optional だが、localStorage/Firestore の
  // 生データは型を通らないため、壊れた値 (非配列・不正要素混入) を描画層まで
  // 通さないよう読み込み時に必ず sanitize 済みにする。
  const sanitizedWaypoints = sanitizeWaypoints(next.evacuation?.waypoints);
  if (sanitizedWaypoints !== next.evacuation?.waypoints) {
    next = {
      ...next,
      evacuation: { ...next.evacuation, waypoints: sanitizedWaypoints },
    };
  }
  // テキストパネル本文に埋め込まれた旧公式名を整形後の名前へ追随させる。
  // placeId から生の公式名を復元して照合する (placeName が既に整形済みでも効く)。
  // 自動生成文の名前だけ差し替わり、ユーザーが本文を書き換えていれば一致せず不変。
  const rawName = rawPlaceNameFromId(next.evacuation?.placeId);
  const cleanedRaw = rawName ? cleanPlaceLabel(rawName) : '';
  if (
    rawName &&
    cleanedRaw !== rawName &&
    next.textPanel?.body?.includes(rawName)
  ) {
    next = {
      ...next,
      textPanel: {
        ...next.textPanel,
        body: next.textPanel.body.split(rawName).join(cleanedRaw),
      },
    };
  }
  return next;
}

/**
 * 避難先 id (`{都道府県}-{公式名}-{連番}`) から生の公式名を復元する。
 * 例: 「神奈川県-(公式名)-0」→ 「(公式名)」。先頭の都道府県と末尾の連番だけ剥がす。
 * 整形前の名前が必要な移行 (テキストパネル本文の追随) に使う。
 */
function rawPlaceNameFromId(placeId: string | undefined): string {
  if (!placeId) return '';
  return placeId.replace(/-\d+$/, '').replace(/^[^-]+-/, '');
}

function defaultMapStateForRoute(propertyCoords: LngLat, ev: EvacuationLink) {
  // 物件位置と避難場所中心の中点を mapState.center に。zoomは距離で調整。
  const distance = ev.distance ?? 0;
  // 経路点があればその外接, なければ propertyCoordsのみ
  const route = ev.routePolyline?.coordinates ?? [
    [propertyCoords.lng, propertyCoords.lat] as [number, number],
  ];
  const lngs = route.map((c) => c[0]);
  const lats = route.map((c) => c[1]);
  const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  // 距離に応じてzoomをざっくり (1500m≈zoom 14, 750m≈15, 350m≈16)
  let zoom = 14.5;
  if (distance > 0) {
    if (distance < 300) zoom = 16;
    else if (distance < 700) zoom = 15.2;
    else if (distance < 1200) zoom = 14.5;
    else zoom = 13.8;
  }
  return {
    center: [centerLng, centerLat] as [number, number],
    zoom,
    bearing: 0,
    pitch: 0,
  };
}

function defaultTextPanel(placeName: string) {
  const body = [
    `大火災発生時の避難場所として、「${placeName || '避難場所'}」があります。`,
    '災害時には周囲の状況を考慮し、公的な指示又は御自身の判断等を優先してください。',
  ].join('\n\n');
  return {
    templateId: 'fire' as const,
    title: '災害時の避難対応',
    body,
    emphasis: ['公的な指示', '御自身の判断'],
  };
}

export interface CreatePropertyInput {
  name: string;
  /** Mapbox Geocoding APIで正規化された住所 */
  formattedAddress: string;
  /** 住所をジオコーディングした座標 */
  coords: LngLat;
  /** 選択済みの避難場所情報 */
  evacuation: EvacuationLink;
  /** 物件種別 (R2)。新規作成 step1 のラジオから。未設定=種別不明。 */
  propertyType?: PropertyKind;
  /** 物件マスタ (HubSpot) との紐付け。マスタから選んだ場合のみ入る。 */
  hubspotPropertyId?: string;
  /** 物件の建物形状 (一括取込で先に取ったもの)。 */
  footprint?: Property['footprint'];
  /** 一括取込の記録。 */
  bulkImport?: Property['bulkImport'];
}

/** 既定物件 (evacuation-seeds.json) のディープコピー (imported module を書き換えないため)。 */
const SEED_PROPERTIES = seedProperties as unknown as Property[];
function cloneSeed(): Property[] {
  return SEED_PROPERTIES.map((p) => JSON.parse(JSON.stringify(p)) as Property);
}

/**
 * ストアが実際に生成済みかどうか (ログアウト flush 用)。未訪問のままログアウト
 * した場合に、flush のためだけに Firestore 初期化を走らせない (signOut と競合させない)。
 */
let storeInstantiated = false;

/**
 * src の値を dst へ即時反映する小ヘルパ (immediate)。別ユーザー再ログイン時に
 * crud を張り替えるため停止関数を返す (再バインド前に必ず呼ぶこと)。
 */
function watchBinding<S, D extends S>(src: Ref<D>, dst: Ref<S>): () => void {
  return watch(
    src,
    (v) => {
      dst.value = v;
    },
    { immediate: true },
  );
}

/**
 * ストア未生成なら何もしない、生成済みなら flush する。ログアウト時に未訪問の
 * 物件画面によって Firestore を初期化せず、signOut との競合を回避する。
 */
export async function flushPropertyStoreIfActive(): Promise<void> {
  if (!storeInstantiated) return;
  const store = usePropertyStore();
  await store.flushAll();
}

export const usePropertyStore = defineStore('property', () => {
  // ストア生成時にフラグをセット (defineStore は一度だけ実行される)
  storeInstantiated = true;

  const firestoreMode = isFirebaseConfigured();
  const crud = shallowRef<
    PersistedCrud<Property> | FirestoreCrud<Property> | undefined
  >(undefined);
  const properties = computed(() => crud.value?.items.value ?? []);
  const ready = ref(false);
  const saveState = ref<SaveState | 'local'>('local');

  /**
   * fetch 進行中状態 (transient、localStorage非永続化)。
   * MapCanvas / EvacuationTab で「取得中...」UI表示に使う。
   * propertyId をキーにして property単位で管理。
   */
  const polygonLoading = ref<Record<string, boolean>>({});
  const routeLoading = ref<Record<string, boolean>>({});

  /**
   * 新規作成直後の「初回表示で地図を内容に自動フィット」対象の物件 id
   * (transient、localStorage 非永続化)。
   * createProperty で立て、MapCanvas が map ready + polygon/route 解決完了後に
   * consumeInitialFit で一度だけ消費する。リロード・既存物件では発火しない
   * (保存済み mapState を上書きしない)。
   */
  const needsInitialFit = new Set<string>();

  /** 初回自動フィットのフラグを消費する (立っていれば降ろして true)。 */
  function consumeInitialFit(propertyId: string): boolean {
    return needsInitialFit.delete(propertyId);
  }

  /**
   * requestAutoFit の発火を MapCanvas の watch で拾うための反応性トリガー。
   * needsInitialFit は非リアクティブな Set なので、これが無いと「loading フラグが
   * 全て降りた後に要求された」フィットは誰にも消費されない (経路確定→フィットが
   * 発火しない実バグがあった)。watch は tick とフラグの両方を見る = 要求が
   * loading 中でも後でも取りこぼさない。
   */
  const autoFitTick = ref(0);

  /**
   * 自動フィットを要求する (新規作成以外の起点用)。
   * 範囲/経路の再取得「成功」時や経路確定時に呼ばれ、MapCanvas が
   * 取得完了 (loading フラグ解除) 後に consumeInitialFit で消費してフィットする。
   * 失敗時は呼ばれない = ユーザーの手動フレーミングを無駄に壊さない。
   */
  function requestAutoFit(propertyId: string): void {
    needsInitialFit.add(propertyId);
    autoFitTick.value++;
  }

  function setPolygonLoading(propertyId: string, loading: boolean): void {
    if (loading) polygonLoading.value[propertyId] = true;
    else delete polygonLoading.value[propertyId];
  }
  function setRouteLoading(propertyId: string, loading: boolean): void {
    if (loading) routeLoading.value[propertyId] = true;
    else delete routeLoading.value[propertyId];
  }

  // ===== 経路候補 (Mapbox 代替経路から選択)。transient = localStorage 非永続 =====
  const routeCandidates = ref<Record<string, AccurateRouteResult[]>>({});
  /**
   * 仮選択中の候補 index。**採用済みの index ではない。**
   * 候補はここを動かすだけでは経路データに書き込まれず、commitPendingRoute で初めて
   * property に入る。地図は仮選択を「確定後と同じ描画」でプレビューする。
   */
  const pendingRouteIndex = ref<Record<string, number>>({});

  /** 仮選択中の候補 (無ければ null)。地図のプレビューが読む。 */
  function pendingRouteCandidate(
    propertyId: string,
  ): AccurateRouteResult | null {
    const i = pendingRouteIndex.value[propertyId] ?? -1;
    return routeCandidates.value[propertyId]?.[i] ?? null;
  }

  /**
   * 経路候補 (最大3本) を取得し、1本目を**仮選択**する (経路データには書かない)。
   * 取得中は routeLoading (MapCanvas の取得中モーダル) を立てる。
   * 失敗は throw (呼び出し側でエラー表示)。
   */
  /**
   * 経路候補取得のエポック (物件ごと)。取得中に避難場所を再選択すると新しい取得が
   * 走る — 古い取得の完了が新しい候補を上書きしないよう、開始時のエポックを覚えて
   * 完了時に進んでいたら書き込まない。
   */
  const routeFetchEpoch: Record<string, number> = {};

  /**
   * 経路確定時の知らせ (経由点を大きく寄せたとき)。物件 id ごとの transient state。
   * サイドバー (EvacuationTab) が表示する。次の経路取得で消す。
   */
  const routeNotice = ref<Record<string, string>>({});

  async function fetchRouteCandidates(propertyId: string): Promise<void> {
    const p = getProperty(propertyId);
    if (!p) return;
    const token = (routeFetchEpoch[propertyId] =
      (routeFetchEpoch[propertyId] ?? 0) + 1);
    delete routeNotice.value[propertyId];
    setRouteLoading(propertyId, true);
    try {
      // 避難先の代表点: マスターの point ?? polygon 重心 ?? evacuation.coords。
      // (入口・境界の解決は fetchAccurateRouteCandidates 側が polygon から行う)
      // evacuation.coords フォールバックはカスタム避難先 (placeId='custom:…' で
      // マスター逆引き不能) に必須 — 無いと経路が一切引けない。
      const places = await loadEvacuationPlaces();
      const place = places.find((x) => x.id === p.evacuation.placeId);
      const ring = p.evacuation.polygon?.coordinates;
      const dest =
        place?.point ??
        (ring && ring.length >= 3 ? polygonCentroid(ring) : null) ??
        p.evacuation.coords ??
        null;
      if (!dest) {
        throw new Error(
          '経路の終点が決定できません (避難場所の範囲か地点が必要です)',
        );
      }
      const cands = await fetchAccurateRouteCandidates(
        p.coords,
        dest,
        areaAsRings(p.evacuation.polygon),
        p.footprint,
        p.evacuation.waypoints ?? [],
      );
      if (routeFetchEpoch[propertyId] !== token) return; // 古い取得 → 破棄
      routeCandidates.value[propertyId] = cands;
      pendingRouteIndex.value[propertyId] = cands.length > 0 ? 0 : -1;
    } finally {
      if (routeFetchEpoch[propertyId] === token) {
        setRouteLoading(propertyId, false);
      }
    }
  }

  /** 候補 i を仮選択する (サイドバーのチップ / 地図の破線クリック共通)。書き込まない。 */
  function setPendingRoute(propertyId: string, index: number): void {
    if (!routeCandidates.value[propertyId]?.[index]) return;
    pendingRouteIndex.value[propertyId] = index;
  }

  /**
   * 仮選択中の候補を経路として確定する (ここで初めて property に書く)。
   * 候補一覧は消さない = 確定後も選び直せる。表示を終うのは clearRouteCandidates。
   *
   * 経路の「確定」では地図の自動フィットはしない — 確定した瞬間に視点が飛ぶのを
   * 防ぐ (ユーザー要望)。避難場所の変更時にだけ新経路へフィットさせたいので、
   * そのフィットは applyPlaceChange (EvacuationTab) が明示的に requestAutoFit する。
   */
  function commitPendingRoute(propertyId: string): void {
    const p = getProperty(propertyId);
    const cand = pendingRouteCandidate(propertyId);
    if (!p || !cand) return;
    // 経由点のピンを確定した経路の上へ移す (v2 と同じ規則。入口の経由点は動かさない)。
    // Mapbox は経由点を上限なく道へ吸着するので、道の無い所に置いたピンは線から外れる。
    const wps = p.evacuation.waypoints;
    const snapped = wps?.length
      ? snapWaypointsForRoute(
          wps,
          cand.coordinates,
          p.evacuation.polygon?.coordinates ?? null,
        )
      : null;
    if (snapped?.notice) routeNotice.value[propertyId] = snapped.notice;
    else delete routeNotice.value[propertyId];
    updateProperty(propertyId, {
      evacuation: {
        ...p.evacuation,
        routePolyline: { coordinates: cand.coordinates },
        distance: cand.distance,
        routeMode: 'auto',
        ...(snapped ? { waypoints: snapped.waypoints } : {}),
      },
    });
  }

  /** 経路候補の表示を終う (確定後・避難場所の変更時など)。 */
  function clearRouteCandidates(propertyId: string): void {
    delete routeCandidates.value[propertyId];
    delete pendingRouteIndex.value[propertyId];
  }

  // ===== 経由点(必ず通過する点)配置モード (transient、localStorage非永続) =====
  //
  // v2 (PropertyEditorV2) はサイドバーと地図が同一ビュー内なので MapCanvasV2 へ
  // props/emit で直結できるが、v1 はサイドバー (EditorSidebar → EvacuationTab) と
  // 地図 (PropertyEditor → MapCanvas) が別ビューの兄弟component であり、共通の親
  // PropertyEditor.vue はこのタスクの変更対象外 (プロパティストアと
  // EvacuationTab.vue/MapCanvas.vue の3ファイルのみ変更可)。props/emit で直結すると
  // 「誰も渡さない prop」「誰も聞かない emit」になり機能しない。
  // そのため既存の polygonLoading/routeCandidates と同じ「store 越し」の経路
  // (property id をキーにした transient state) を使う: EvacuationTab (トグル/削除/
  // 全消去) と MapCanvas (クリックで追加/ドラッグで移動) が直接この store を読み書きする。
  const waypointMode = ref<Record<string, boolean>>({});

  function setWaypointMode(propertyId: string, on: boolean): void {
    if (on) waypointMode.value[propertyId] = true;
    else delete waypointMode.value[propertyId];
  }
  function toggleWaypointMode(propertyId: string): void {
    setWaypointMode(propertyId, !waypointMode.value[propertyId]);
  }

  /** 経由点を1点追加する (地図クリック)。経路の再計算はしない (明示的トリガーのみ)。 */
  function addWaypoint(propertyId: string, lngLat: LngLat): void {
    const p = getProperty(propertyId);
    if (!p) return;
    updateProperty(propertyId, {
      evacuation: {
        ...p.evacuation,
        waypoints: [
          ...(p.evacuation.waypoints ?? []),
          { lng: lngLat.lng, lat: lngLat.lat },
        ],
      },
    });
    // 1点置くたびに配置モードを解除する (連続配置ではなく都度ボタンで再開する仕様)。
    setWaypointMode(propertyId, false);
  }

  /** 経由点をドラッグで移動する。 */
  function moveWaypoint(
    propertyId: string,
    index: number,
    lngLat: LngLat,
  ): void {
    const p = getProperty(propertyId);
    const wp = p?.evacuation.waypoints;
    if (!p || !wp?.[index]) return;
    const next = [...wp];
    next[index] = { lng: lngLat.lng, lat: lngLat.lat };
    updateProperty(propertyId, {
      evacuation: { ...p.evacuation, waypoints: next },
    });
  }

  /** 経由点を1点削除する (サイドバーの ✕)。 */
  function removeWaypoint(propertyId: string, index: number): void {
    const p = getProperty(propertyId);
    const wp = p?.evacuation.waypoints;
    if (!p || !wp) return;
    const next = wp.filter((_, i) => i !== index);
    updateProperty(propertyId, {
      evacuation: { ...p.evacuation, waypoints: next.length ? next : undefined },
    });
  }

  /** 経由点を全消去する。 */
  function clearWaypoints(propertyId: string): void {
    const p = getProperty(propertyId);
    if (!p) return;
    updateProperty(propertyId, {
      evacuation: { ...p.evacuation, waypoints: undefined },
    });
  }

  function getProperty(id: string): Property | undefined {
    return crud.value?.get(id);
  }

  /**
   * 物件を作成する。
   *
   * - ジオコーディングは呼び出し元 (PropertyNew) で完了済みであることを前提に、
   *   座標 + 正規化住所 + 選択済み避難場所を受け取って即座に保存する。
   *   これにより createProperty は同期的にも近い軽量処理になり、テストしやすい。
   * - mapState 初期値は「物件と避難場所代表点が両方収まる」中点に設定。
   */
  function createProperty(input: CreatePropertyInput): Property {
    const draft: DraftProperty = {
      name: input.name,
      address: input.formattedAddress,
      coords: input.coords,
      buildingIcon: 'default',
      evacuation: input.evacuation,
      mapState: defaultMapStateForRoute(input.coords, input.evacuation),
      labels: [],
      textPanel: defaultTextPanel(input.evacuation.placeName),
      propertyType: input.propertyType,
      hubspotPropertyId: input.hubspotPropertyId,
      // Firestore に undefined を書かないよう、あるときだけ載せる
      ...(input.footprint ? { footprint: input.footprint } : {}),
      ...(input.bulkImport ? { bulkImport: input.bulkImport } : {}),
    };
    const property: Property = {
      ...draft,
      id: generateId('prop'),
      createdBy: currentEditor() ?? 'local',
      updatedBy: currentEditor() ?? 'local',
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    crud.value?.insert(property);
    // 初回表示でエディタ側が fitToContent を自動実行するための transient フラグ
    needsInitialFit.add(property.id);
    return property;
  }

  function updateProperty(
    id: string,
    patch: Partial<Omit<Property, 'id' | 'createdAt' | 'createdBy'>>,
  ): void {
    // updatedAt は crud.update が現在時刻へ更新する。updatedBy は withEditStamp。
    crud.value?.update(id, withEditStamp(patch));
  }

  function deleteProperty(id: string): void {
    crud.value?.remove(id);
  }

  function flushAll(): Promise<void> {
    const c = crud.value;
    return c && 'flushAll' in c ? c.flushAll() : Promise.resolve();
  }

  if (firestoreMode) {
    // ---- Firestore モード (§4.3) — propertyStoreV2 と同型 ----
    const auth = useAuthStore();

    /** 現在の crud がどの email にバインドされているか (別ユーザー再ログイン検知用)。 */
    let boundEmail: string | null = null;
    /** ready/saveState の watchBinding 停止関数 (再バインド前に解除する)。 */
    let stopBindings: Array<() => void> = [];
    /** 現在の fsCrud の dispose (次のユーザーへ張り替える際に呼ぶ)。 */
    let disposeCurrent: (() => void) | null = null;

    const initFirestore = (email: string, displayName: string | null): void => {
      for (const stop of stopBindings) stop();
      stopBindings = [];
      const fsCrud = createFirestoreCrud<Property>({
        kind: 'properties_v1',
        email,
        displayName,
        normalize: normalizeLegacyProperty,
        deferLoad: true,
      });
      crud.value = fsCrud;
      stopBindings.push(watchBinding(fsCrud.ready, ready));
      stopBindings.push(watchBinding(fsCrud.saveState, saveState));
      disposeCurrent = fsCrud.dispose;
      boundEmail = email;
      void (async () => {
        try {
          await ensureSeedsV1(email, displayName); // §6-5 フラグdoc + transaction
        } catch (e) {
          // シード注入の失敗 (transaction競合/瞬断) で閲覧まで道連れにしない。
          console.warn('[propertyStore] シード注入に失敗 (閲覧は継続)', e);
        }
        fsCrud.startLoad();
      })();
    };

    // auth.status の遷移を継続監視する (ログアウト→別ユーザー再ログインで張り替え)。
    watch(
      () => auth.status,
      (status) => {
        if (status === 'ready' && auth.email && auth.email !== boundEmail) {
          disposeCurrent?.();
          initFirestore(auth.email, auth.displayName);
        } else if (status !== 'ready') {
          boundEmail = null;
          ready.value = false;
        }
      },
      { immediate: true },
    );
  } else {
    // ---- localStorage モード (現行どおり) ----
    // 既定物件は **初回のみ** 投入する (localStorage が空 = 初回)。2回目以降に
    // 入れ直さないのは、ユーザーが削除した物件を勝手に復活させないため。
    const firstRun = localStorage.getItem(STORAGE_KEY) === null;
    crud.value = createPersistedCrud<Property>({
      storageKey: STORAGE_KEY,
      label: '物件データ',
      initial: cloneSeed, // 初回のみ既定物件
      normalize: normalizeLegacyProperty,
    });
    if (firstRun) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(properties.value));
      } catch (e) {
        console.warn('既定物件の初期保存に失敗しました', e);
      }
    }
    ready.value = true;
  }

  return {
    properties,
    ready,
    saveState,
    flushAll,
    polygonLoading,
    routeLoading,
    getProperty,
    createProperty,
    consumeInitialFit,
    requestAutoFit,
    autoFitTick,
    routeCandidates,
    pendingRouteIndex,
    pendingRouteCandidate,
    fetchRouteCandidates,
    setPendingRoute,
    commitPendingRoute,
    clearRouteCandidates,
    routeNotice,
    waypointMode,
    setWaypointMode,
    toggleWaypointMode,
    addWaypoint,
    moveWaypoint,
    removeWaypoint,
    clearWaypoints,
    updateProperty,
    deleteProperty,
    setPolygonLoading,
    setRouteLoading,
  };
});
